import {storeToRefs} from 'pinia';
import {computed} from 'vue';

import {useTariffsStore} from '@/store/tariffs';
import {getGroupedRow as groupedRow, getRow as row} from '@/tariffs/table';
import {cellFormatters} from '@/tariffs/cell_formatters';

import {usePersonalizedTariffFormStore} from '@/store/forms/personalized_tariffs';
import {
    dotDotDotWhenFalsy,
    formattedValue,
} from '@/utils/formatters';

export const personalizedTariffs = {
    getRows: () => {
        const formStore = usePersonalizedTariffFormStore();
        const {
            formData,
            yearlyWorkTimeMinusDaysOff,
            livingWages,
            livingWageDifferences,
            socialSecurityTaxes,
            overheadGrandTotal,
        } = storeToRefs(formStore);

        const fmt = cellFormatters.value;

        const tariffsStore = useTariffsStore();
        const {yearly, monthly, weekly, hourly} = storeToRefs(tariffsStore);
        const {occupationCostsRowIsVisible} = tariffsStore;

        return [
            groupedRow(
                {
                    name: 'page2_subheading_net_lw',
                    defaultToggle: true,
                },
                [
                    row(
                        {
                            name: 'page3_fixed_net_lw_hour',
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerHour.display(hourly.value.livingWages.net),
                            null,
                        ]
                    ),
                    row(
                        {
                            name: 'page3_fixed_net_lw_month',
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.display(monthly.value.livingWages.net),
                            null,
                        ]
                    ),
                    row(
                        {
                            name: 'page3_fixed_net_lw_year',
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerYear.display(yearly.value.livingWages.net),
                            null,
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_nr_working_days_py',
                            toggleable: true,
                        },
                        [
                            fmt.daysPerYear.display(
                                formattedValue(formData.value.workingDaysPerYear)
                            ),
                            fmt.daysPerYear.diff(
                                yearly.value.totalWorkDays,
                                computed(() => formData.value.workingDaysPerYear),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_nr_festive_days_py',
                            toggleable: true,
                        },
                        [
                            fmt.daysPerYear.input(
                                'publicHolidaysPerYear',
                                yearly.value.publicHolidays,
                                yearly.value.totalWorkDays,
                            ),
                            fmt.daysPerYear.diff(
                                yearly.value.publicHolidays,
                                computed(() => formData.value.publicHolidaysPerYear),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_nr_leavedays_py',
                            toggleable: true,
                        },
                        [
                            fmt.daysPerYear.input(
                                'leaveDaysPerYear',
                                yearly.value.leaveDays,
                                yearly.value.totalWorkDays,
                            ),
                            fmt.daysPerYear.diff(
                                yearly.value.leaveDays,
                                computed(() => formData.value.leaveDaysPerYear),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_nr_days_pw',
                            toggleable: true,
                        },
                        [
                            fmt.daysPerWeek.input(
                                'workingDaysPerWeek',
                                weekly.value.workDays,
                                7
                            ),
                            fmt.daysPerWeek.diff(
                                weekly.value.workDays,
                                computed(() => formData.value.workingDaysPerWeek),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_nr_hours_pw',
                            toggleable: true,
                        },
                        [
                            fmt.hoursPerWeek.input(
                                'workingHoursPerWeek',
                                weekly.value.fullTimeWorkingHours,
                                99,
                            ),
                            fmt.hoursPerWeek.diff(
                                weekly.value.fullTimeWorkingHours,
                                computed(() => formData.value.workingHoursPerWeek),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_nr_working_days_py',
                            toggleable: true,
                        },
                        [
                            fmt.daysPerYear.display(dotDotDotWhenFalsy(yearlyWorkTimeMinusDaysOff.value.days)),
                            null,
                        ]
                    ),
                    row(
                        {
                            name: 'page3_nr_working_days_py',
                            toggleable: true,
                        },
                        [
                            fmt.hoursPerYear.display(dotDotDotWhenFalsy(yearlyWorkTimeMinusDaysOff.value.hours)),
                            null,
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_lt_without_cost_without_tax_hour',
                            hasTotalAmount: true,
                        },
                        [
                            fmt.currencyPerHour.display(
                                livingWages.value.withoutOverhead.hourly
                            ),
                            fmt.currencyPerHour.diff(
                                computed(() => livingWageDifferences.value.withoutOverhead.hourly.original),
                                computed(() => livingWageDifferences.value.withoutOverhead.hourly.input),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_lt_without_cost_without_tax_month',
                            hasTotalAmount: true,
                        },
                        [
                            fmt.currencyPerMonth.display(
                                monthly.value.livingWages.net,
                            ),
                            null,
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_lt_without_cost_without_tax_year',
                            hasTotalAmount: true,
                        },
                        [
                            fmt.currencyPerYear.display(
                                yearly.value.livingWages.net,
                            ),
                            null,
                        ]
                    ),
                ],
            ),
            groupedRow(
                {
                    name: 'page3_job_related_costs',
                    defaultToggle: true,
                    enabled: occupationCostsRowIsVisible(),
                },
                [
                    row(
                        {
                            name: 'page3_user_cost_computer',
                            enabled: occupationCostsRowIsVisible('computer'),
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.input(
                                'computerMonthlyCosts',
                                monthly.value.jobRelatedCosts.computer,
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.jobRelatedCosts.computer,
                                computed(() => formData.value.computerMonthlyCosts),
                            ),
                        ],
                    ),
                    row(
                        {
                            name: 'page3_user_cost_phone',
                            enabled: occupationCostsRowIsVisible('phone'),
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.input(
                                'phoneMonthlyCosts',
                                monthly.value.jobRelatedCosts.phone,
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.jobRelatedCosts.phone,
                                computed(() => formData.value.phoneMonthlyCosts),
                            ),
                        ],
                    ),
                    row(
                        {
                            name: 'page3_user_cost_car',
                            enabled: occupationCostsRowIsVisible('car'),
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.input(
                                'carMonthlyCosts',
                                monthly.value.jobRelatedCosts.car,
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.jobRelatedCosts.car,
                                computed(() => formData.value.carMonthlyCosts),
                            ),
                        ],
                    ),
                    row(
                        {
                            name: 'page3_user_cost_scooter',
                            enabled: occupationCostsRowIsVisible('scooter'),
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.input(
                                'scooterMonthlyCosts',
                                monthly.value.jobRelatedCosts.scooter,
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.jobRelatedCosts.scooter,
                                computed(() => formData.value.scooterMonthlyCosts),
                            ),
                        ],
                    ),
                    row(
                        {
                            name: 'page3_user_cost_ebicycle',
                            enabled: occupationCostsRowIsVisible('electricBicycle'),
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.input(
                                'electricBicycleMonthlyCosts',
                                monthly.value.jobRelatedCosts.electricBicycle,
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.jobRelatedCosts.electricBicycle,
                                computed(() => formData.value.electricBicycleMonthlyCosts),
                            ),
                        ],
                    ),
                    row(
                        {
                            name: 'page3_user_cost_bicycle',
                            enabled: occupationCostsRowIsVisible('bicycle'),
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.input(
                                'bicycleMonthlyCosts',
                                monthly.value.jobRelatedCosts.bicycle,
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.jobRelatedCosts.bicycle,
                                computed(() => formData.value.bicycleMonthlyCosts),
                            ),
                        ],
                    ),
                    row(
                        {
                            name: 'page3_user_cost_mix',
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.input(
                                'miscMonthlyCosts'
                            ),
                            null,
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_total_cost_hour',
                            enabled: occupationCostsRowIsVisible(),
                            hasTotalAmount: true,
                        },
                        [
                            fmt.currencyPerHour.display(
                                computed(() => {
                                    return overheadGrandTotal.value.jobCosts.hourly;
                                })
                            ),
                            fmt.currencyPerHour.diff(
                                hourly.value.jobRelatedCosts.total,
                                computed(() => overheadGrandTotal.value.jobCosts.hourly),
                            ),
                        ],
                    ),
                    row(
                        {
                            name: 'page3_user_total_cost_month',
                            enabled: occupationCostsRowIsVisible(),
                            hasTotalAmount: true,
                        },
                        [
                            fmt.currencyPerMonth.display(
                                computed(() => {
                                    return overheadGrandTotal.value.jobCosts.monthly;
                                })
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.jobRelatedCosts.total,
                                computed(() => overheadGrandTotal.value.jobCosts.monthly),
                            ),
                        ],
                    ),
                    row(
                        {
                            name: 'page3_user_total_cost_year',
                            enabled: occupationCostsRowIsVisible(),
                            hasTotalAmount: true,
                        },
                        [
                            fmt.currencyPerYear.display(
                                computed(() => {
                                    return overheadGrandTotal.value.jobCosts.yearly;
                                })
                            ),
                            fmt.currencyPerYear.diff(
                                yearly.value.jobRelatedCosts.total,
                                computed(() => overheadGrandTotal.value.jobCosts.yearly),
                            ),
                        ],
                    ),
                ]
            ),
            groupedRow(
                {
                    name: 'page3_user_jobrelated_time',
                    defaultToggle: true,
                },
                [
                    row(
                        {
                            name: 'page3_user_time_administration',
                            toggleable: true,
                        },
                        [
                            fmt.hoursPerWeek.input(
                                'administrationHoursPerWeek',
                                weekly.value.jobRelatedHours.administration,
                                99,
                            ),
                            fmt.hoursPerWeek.diff(
                                weekly.value.jobRelatedHours.administration,
                                computed(() => formData.value.administrationHoursPerWeek),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_time_acquisition',
                            toggleable: true,
                        },
                        [
                            fmt.hoursPerWeek.input(
                                'acquisitionHoursPerWeek',
                                weekly.value.jobRelatedHours.acquisition,
                                99,
                            ),
                            fmt.hoursPerWeek.diff(
                                weekly.value.jobRelatedHours.acquisition,
                                computed(() => formData.value.acquisitionHoursPerWeek),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_time_training',
                            toggleable: true,
                        },
                        [
                            fmt.hoursPerWeek.input(
                                'trainingHoursPerWeek',
                                weekly.value.jobRelatedHours.training,
                                99,
                            ),
                            fmt.hoursPerWeek.diff(
                                weekly.value.jobRelatedHours.training,
                                computed(() => formData.value.trainingHoursPerWeek),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_time_waiting_time',
                            toggleable: true,
                        },
                        [
                            fmt.hoursPerWeek.input(
                                'waitingHoursPerWeek',
                                weekly.value.jobRelatedHours.waiting,
                                99,
                            ),
                            fmt.hoursPerWeek.diff(
                                weekly.value.jobRelatedHours.waiting,
                                computed(() => formData.value.waitingHoursPerWeek),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_total_jobrelated_time',
                            hasTotalAmount: true,
                        },
                        [
                            fmt.hoursPerWeek.display(overheadGrandTotal.value.jobRelatedTime.weekly),
                            fmt.hoursPerWeek.diff(
                                weekly.value.jobRelatedHours.total,
                                computed(() => overheadGrandTotal.value.jobRelatedTime.weekly),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_lt_with_cost_overhead_hour',
                            isHighlighted: true,
                        },
                        [
                            fmt.currencyPerHour.display(
                                livingWages.value.withCostsAndOverhead.hourly
                            ),
                            fmt.currencyPerHour.diff(
                                computed(() => livingWageDifferences.value.withCostsAndOverhead.hourly.original),
                                computed(() => livingWageDifferences.value.withCostsAndOverhead.hourly.input),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_lt_with_cost_overhead_month',
                            isHighlighted: true,
                        },
                        [
                            fmt.currencyPerMonth.display(
                                livingWages.value.withCostsAndOverhead.monthly
                            ),
                            fmt.currencyPerMonth.diff(
                                computed(() => livingWageDifferences.value.withCostsAndOverhead.monthly.original),
                                computed(() => livingWageDifferences.value.withCostsAndOverhead.monthly.input),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_lt_with_cost_overhead_year',
                            isHighlighted: true,
                        },
                        [
                            fmt.currencyPerYear.display(
                                livingWages.value.withCostsAndOverhead.yearly
                            ),
                            fmt.currencyPerYear.diff(
                                computed(() => livingWageDifferences.value.withCostsAndOverhead.yearly.original),
                                computed(() => livingWageDifferences.value.withCostsAndOverhead.yearly.input),
                            ),
                        ]
                    ),
                ]
            ),
            groupedRow(
                {
                    name: 'page3_user_cost_social_security',
                    defaultToggle: true,
                    isHeading: true,
                },
                [
                    row(
                        {
                            name: 'page3_user_perc_income_taxes',
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.display(
                                socialSecurityTaxes.value.monthly.incomeTax
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.livingWages.incomeTax,
                                computed(() =>  socialSecurityTaxes.value.monthly.incomeTax),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_reservation_pension',
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.input(
                                'pension__Contribution',
                                monthly.value.livingWages.socialContributions.pension,
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.livingWages.socialContributions.pension,
                                computed(() =>  formData.value.pension__Contribution),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_reservation_sickness',
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.input(
                                'sickness__Contribution',
                                monthly.value.livingWages.socialContributions.sickness,
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.livingWages.socialContributions.sickness,
                                computed(() =>  formData.value.sickness__Contribution),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_reservation_disability',
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.input(
                                'disability__Contribution',
                                monthly.value.livingWages.socialContributions.disability,
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.livingWages.socialContributions.disability,
                                computed(() =>  formData.value.disability__Contribution),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_reservation_no_work',
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.input(
                                'no_work_available__Contribution',
                                monthly.value.livingWages.socialContributions.no_work_available,
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.livingWages.socialContributions.no_work_available,
                                computed(() =>  formData.value.no_work_available__Contribution),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_cost_medical_insurance',
                            toggleable: true,
                        },
                        [
                            fmt.currencyPerMonth.input(
                                'medical_insurance__Contribution',
                                monthly.value.livingWages.socialContributions.medical_insurance,
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.livingWages.socialContributions.medical_insurance,
                                computed(() =>  formData.value.medical_insurance__Contribution),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_total_tax_social_security_hour',
                            hasTotalAmount: true,
                        },
                        [
                            fmt.currencyPerHour.display(
                                socialSecurityTaxes.value.hourly.total
                            ),
                            fmt.currencyPerHour.diff(
                                hourly.value.livingWages.incomeTaxPlusSocialContributions,
                                computed(() => socialSecurityTaxes.value.hourly.total),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_total_tax_social_security_month',
                            hasTotalAmount: true,
                        },
                        [
                            fmt.currencyPerMonth.display(
                                socialSecurityTaxes.value.monthly.total
                            ),
                            fmt.currencyPerMonth.diff(
                                monthly.value.livingWages.incomeTaxPlusSocialContributions,
                                computed(() => socialSecurityTaxes.value.monthly.total),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_total_tax_social_security_year',
                            hasTotalAmount: true,
                        },
                        [
                            fmt.currencyPerYear.display(
                                socialSecurityTaxes.value.yearly.total
                            ),
                            fmt.currencyPerYear.diff(
                                yearly.value.livingWages.incomeTaxPlusSocialContributions,
                                computed(() => socialSecurityTaxes.value.yearly.total),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_lt_with_cost_with_tax_hour',
                            isHighlighted: true,
                        },
                        [
                            fmt.currencyPerHour.display(livingWages.value.withTaxes.hourly),
                            fmt.currencyPerHour.diff(
                                hourly.value.livingWages.withIncomeTax,
                                computed(() => livingWages.value.withTaxes.hourly),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_lt_with_cost_with_tax_month',
                            isHighlighted: true,
                        },
                        [
                            fmt.currencyPerMonth.display(livingWages.value.withTaxes.monthly),
                            fmt.currencyPerMonth.diff(
                                monthly.value.livingWages.withIncomeTax,
                                computed(() => livingWages.value.withTaxes.monthly),
                            ),
                        ]
                    ),
                    row(
                        {
                            name: 'page3_user_lt_with_cost_with_tax_year',
                            isHighlighted: true,
                        },
                        [
                            fmt.currencyPerYear.display(livingWages.value.withTaxes.yearly),
                            fmt.currencyPerYear.diff(
                                yearly.value.livingWages.withIncomeTax,
                                computed(() => livingWages.value.withTaxes.yearly),
                            ),
                        ]
                    ),
                ]
            ),
        ];
    },
};
